import { DeductionDetailJson, DeductionExt, updateDeduction } from "@/api/deduction.tsx"
import { LoadingSpinner } from "@/app/loading.tsx"
import { Separator } from "@/components/ui/separator.tsx"
import { Checkbox } from "@/components/ui/checkbox.tsx"
import { Input } from "@/components/ui/input.tsx"
import { CopyableCell } from "@/components/copyable_cell.tsx"
import { classes, exactCurrencyFormatter } from "@/utils"
import { format_custom_deduction_method_label } from "@/utils/format_custom_deduction_method"
import { Pencil } from "lucide-react"
import { useEffect, useState } from "preact/hooks"
import { CategoryCell } from "@/deduction_table"
import { use_days_old_basis } from "@/deduction/aging/aging_state"
import { compute_age_days, format_days_old_detail } from "@/utils/deduction_age"
import { format_deduction_status_label } from "@/utils/deduction_status_labels"
import { differenceInDays, format, parseISO } from "date-fns"
import { toast } from "@/components/ui/use-toast.ts"
import { PromotionDisplay } from "./PromotionDisplay.tsx"
import { DeductionPromotionField } from "./DeductionPromotionField.tsx"
import { OrgConfigState } from "@/org_config/state.tsx"
import { deduction_detail_org_field_active } from "@/deduction_detail/org_field_helpers.ts"
import { FieldType } from "@/api/org_field_config.tsx"
import { triggerRefreshFlag } from "@/refresh_flag.tsx"

/** Method dropdown: AI import uses DEDUCT / Pay Customer Directly; INDIRECT is a standard alternative. Other legacy strings prepend when present on the row. */
const CUSTOM_DEDUCTION_METHOD_OPTIONS = ["DEDUCT", "Pay Customer Directly", "INDIRECT"] as const

function customDeductionMethodOptionsForValue(current: string): string[] {
  const base: string[] = [...CUSTOM_DEDUCTION_METHOD_OPTIONS]
  const trimmed = current.trim()
  if (trimmed !== "" && !base.includes(trimmed)) {
    return [trimmed, ...base]
  }
  return base
}

interface DeductionDetailsSectionProps {
  deduction: DeductionDetailJson
  onUpdate: (updated: DeductionDetailJson) => void
  className?: string
}

interface EditFieldProps {
  id: string
  label: string
  value: string
  onUpdate: (updated: DeductionDetailJson) => void
  deductionId: string
}

export function EditField({ id, label, value: initial, onUpdate, deductionId }: EditFieldProps) {
  let [loading, setLoading] = useState(false)
  let [value, setValue] = useState(initial)

  async function onBlur() {
    if (value === initial) {
      return
    }
    setLoading(true)
    let payload = {
      [id]: value,
    }
    try {
      const updatedDeduction = await updateDeduction(deductionId, payload)
      onUpdate(updatedDeduction)
      toast({
        title: "Update",
        description: `Successfully updated ${label}`,
      })
    } catch (error) {
      toast({
        title: "Failed to update deduction",
        description: error as Error,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div key={id} class="group relative">
      <label for={id} class="text-gray-500 text-sm block">
        {label}
      </label>
      <div class="relative">
        <input
          id={id}
          autocapitalize="off"
          autocomplete="off"
          autocorrect="off"
          type="text"
          class="border-0 p-0 w-full outline-none focus:ring-0 bg-transparent"
          value={value}
          onChange={e => setValue(e.currentTarget.value)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.currentTarget.blur()
            }
          }}
          onBlur={onBlur}
        />
        <Pencil className="w-4 h-4 float-right absolute top-1 right-1 opacity-0 group-honofo:opacity-100 transition-opacity duration-300 pointer-events-none" />
      </div>
      {loading && (
        <div class="w-full absolute h-full top-0 left-0 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      )}
    </div>
  )
}

/** The date fields `clearing_list_month` / `month_end_close_list_month` read: editing one moves the row between lists. */
export function date_field_feeds_clearing_list(id: string): boolean {
  return id === "validated_date" || id === "posting_date"
}

function DateField({ id, label, value: initial, onUpdate, deductionId }: EditFieldProps) {
  let [loading, setLoading] = useState(false)
  let [value, setValue] = useState(initial)

  async function onBlur() {
    if (value === initial) {
      return
    }
    setLoading(true)
    let payload = {
      [id]: value,
    }
    try {
      const updatedDeduction = await updateDeduction(deductionId, payload)
      onUpdate(updatedDeduction)
      // The list behind the modal derives Clearing List from these dates; refetch it so the column
      // follows a manual edit instead of showing the old month until a reload.
      if (date_field_feeds_clearing_list(id)) {
        triggerRefreshFlag()
      }
      toast({
        title: "Update",
        description: `Successfully updated ${label}`,
      })
    } catch (error) {
      toast({
        title: "Failed to update deduction",
        description: error as Error,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div key={id} class="group relative">
      <label for={id} class="text-gray-500 text-sm block">
        {label}
      </label>
      <div class="relative">
        <input
          id={id}
          autocapitalize="off"
          autocomplete="off"
          autocorrect="off"
          type="date"
          class="border-0 p-0 w-full outline-none focus:ring-0 bg-transparent"
          value={value}
          onChange={e => setValue(e.currentTarget.value)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.currentTarget.blur()
            }
          }}
          onBlur={onBlur}
        />
        {/*<Pencil*/}
        {/*  className="w-4 h-4 float-right absolute top-1 right-1 opacity-0 group-honofo:opacity-100 transition-opacity duration-300 pointer-events-none"/>*/}
      </div>
      {loading && (
        <div class="w-full absolute h-full top-0 left-0 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      )}
    </div>
  )
}

const UNFI_WAREHOUSES = [
  "Atlanta",
  "Auburn",
  "Aurora",
  "Chesterfield",
  "Dayville",
  "Gilroy",
  "Greenwood",
  "Howell",
  "Hudson Valley",
  "Iowa",
  "Lancaster/Dallas",
  "Londonderry",
  "Manchester",
  "Moreno Valley",
  "Philadelphia",
  "Racine/Sturtevant",
  "Richburg",
  "Ridgefield",
  "Rocklin",
  "Sarasota",
  "Sarasota North",
  "Twin Cities/Prescott",
  "York",
]

function WarehouseDropdown({ id, label, value: initial, onUpdate, deductionId }: EditFieldProps) {
  let [loading, setLoading] = useState(false)
  let [value, setValue] = useState(initial)

  async function handleChange(e: Event) {
    const newValue = (e.target as HTMLSelectElement).value
    if (newValue === initial) {
      return
    }
    setValue(newValue)
    setLoading(true)
    let payload = {
      [id]: newValue,
    }
    try {
      const updatedDeduction = await updateDeduction(deductionId, payload)
      onUpdate(updatedDeduction)
      toast({
        title: "Update",
        description: `Successfully updated ${label}`,
      })
    } catch (error) {
      toast({
        title: "Failed to update deduction",
        description: error as Error,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div key={id} class="group relative">
      <label for={id} class="text-gray-500 text-sm block">
        {label}
      </label>
      {loading ? (
        <div class="w-full absolute h-full top-0 left-0 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      ) : (
        <div class="relative">
          <select
            id={id}
            class="border-0 p-0 w-full outline-none focus:ring-0 bg-transparent appearance-none pr-8"
            value={value}
            onChange={handleChange}>
            <option value=""></option>
            {UNFI_WAREHOUSES.map(warehouse => (
              <option key={warehouse} value={warehouse}>
                {warehouse}
              </option>
            ))}
          </select>
          {/*<ChevronDown className="w-4 h-4 absolute top-1 right-1 pointer-events-none text-gray-400" />*/}
        </div>
      )}
    </div>
  )
}

interface SelectFieldProps {
  id: string
  label: string
  value: string
  options: readonly string[]
  onUpdate: (updated: DeductionDetailJson) => void
  deductionId: string
  getOptionLabel?: (option: string) => string
}

function SelectField({
  id,
  label,
  value: initial,
  options,
  onUpdate,
  deductionId,
  getOptionLabel,
}: SelectFieldProps) {
  let [loading, setLoading] = useState(false)
  let [value, setValue] = useState(initial)

  async function handleChange(e: Event) {
    const newValue = (e.target as HTMLSelectElement).value
    if (newValue === value) {
      return
    }
    setValue(newValue)
    setLoading(true)
    try {
      const updatedDeduction = await updateDeduction(deductionId, { [id]: newValue })
      onUpdate(updatedDeduction)
      toast({
        title: "Update",
        description: `Successfully updated ${label}`,
      })
    } catch (error) {
      setValue(initial)
      toast({
        title: "Failed to update deduction",
        description: error as Error,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div class="group relative">
      <label for={id} class="text-gray-500 text-sm block">
        {label}
      </label>
      {loading ? (
        <div class="w-full absolute h-full top-0 left-0 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      ) : (
        <select
          id={id}
          class="border-0 p-0 w-full outline-none focus:ring-0 bg-transparent appearance-none"
          value={value}
          onChange={handleChange}>
          <option value=""></option>
          {options.map(option => (
            <option key={option} value={option}>
              {getOptionLabel ? getOptionLabel(option) : option}
            </option>
          ))}
        </select>
      )}
    </div>
  )
}

// UNFI-specific: Display tracking number from WeeklyStatusReportRow
interface PendingFieldProps {
  pending: boolean
  deductionId: string
  onUpdate: (updated: DeductionDetailJson) => void
}

function PendingField({ pending, deductionId, onUpdate }: PendingFieldProps) {
  const [loading, setLoading] = useState(false)
  const [value, setValue] = useState(pending)

  async function handleToggle(checked: boolean) {
    setValue(checked)
    setLoading(true)
    try {
      const updated = await updateDeduction(deductionId, { pending: checked })
      onUpdate(updated)
      toast({
        title: "Update",
        description: `Deduction marked as ${checked ? "pending" : "not pending"}`,
      })
    } catch (error) {
      setValue(!checked)
      toast({
        title: "Failed to update deduction",
        description: error as Error,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div class="group relative">
      <label class="text-gray-500 text-sm block mb-1">Pending</label>
      <div class="flex items-center gap-2">
        <Checkbox
          checked={value}
          // @ts-ignore - Checkbox onCheckedChange type
          onCheckedChange={handleToggle}
          disabled={loading}
        />
        <span class="text-sm text-gray-700">
          {value ? "Excluded from analytics, not yet paid" : "Paid, included in analytics"}
        </span>
      </div>
    </div>
  )
}

function TrackingNumberField({ value }: { value: string }) {
  return (
    <div key="tracking_number" class="group relative">
      <label class="text-gray-500 text-sm block">UNFI Dispute Tracking Number</label>
      <div class="border-0 p-0 w-full outline-none focus:ring-0 bg-transparent">
        <CopyableCell value={value} title="Copy UNFI Dispute Tracking Number" />
      </div>
    </div>
  )
}

/** Read-only, copyable: accounting quotes this number when pulling backup out of their ledger. */
function AccountingIdField({ label, value }: { label: string; value: string }) {
  return (
    <div key="remote_accounting_id" class="group relative">
      <label class="text-gray-500 text-sm block">{label}</label>
      <div class="border-0 p-0 w-full outline-none focus:ring-0 bg-transparent">
        <CopyableCell value={value} title={`Copy ${label}`} />
      </div>
    </div>
  )
}

interface AdminFeeFieldProps {
  admin_fee: number | null | undefined
  deductionId: string
  onUpdate: (updated: DeductionDetailJson) => void
}

export function format_admin_fee_display_value(admin_fee: number | null | undefined): string {
  if (admin_fee == null || !Number.isFinite(admin_fee)) {
    return ""
  }
  return exactCurrencyFormatter(admin_fee)
}

function format_admin_fee_edit_value(admin_fee: number | null | undefined): string {
  if (admin_fee == null || !Number.isFinite(admin_fee)) {
    return ""
  }
  return String(admin_fee)
}

export function parse_admin_fee_input_value(value: string): number | null | undefined {
  const trimmed = value.trim()
  if (trimmed === "") {
    return null
  }
  const parsed = Number(trimmed.replace(/[$,]/g, ""))
  return Number.isFinite(parsed) ? parsed : undefined
}

function admin_fee_values_match(a: number | null | undefined, b: number | null | undefined): boolean {
  if (a == null || b == null) {
    return a == null && b == null
  }
  return Math.round(a * 100) === Math.round(b * 100)
}

function AdminFeeField({ admin_fee, deductionId, onUpdate }: AdminFeeFieldProps) {
  const [value, setValue] = useState(() => format_admin_fee_display_value(admin_fee))
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setValue(format_admin_fee_display_value(admin_fee))
  }, [deductionId, admin_fee])

  async function handleBlur() {
    const parsed = parse_admin_fee_input_value(value)
    if (parsed === undefined) {
      toast({
        variant: "destructive",
        title: "Invalid admin fee",
        description: "Enter a valid dollar amount or leave blank to clear.",
      })
      setValue(format_admin_fee_display_value(admin_fee))
      return
    }
    if (admin_fee_values_match(parsed, admin_fee)) {
      setValue(format_admin_fee_display_value(admin_fee))
      return
    }

    setLoading(true)
    try {
      const payload: { admin_fee: number | null } = { admin_fee: parsed }
      const updatedDeduction = await updateDeduction(deductionId, payload)
      setValue(format_admin_fee_display_value(updatedDeduction.admin_fee))
      onUpdate(updatedDeduction)
      toast({
        title: "Update",
        description: "Successfully updated Admin fee",
      })
    } catch (error) {
      setValue(format_admin_fee_display_value(admin_fee))
      toast({
        title: "Failed to update deduction",
        description: error as Error,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div key="admin_fee" class="group relative">
      <label for="admin_fee" class="text-gray-500 text-sm block">
        Admin fee
      </label>
      <div class="relative">
        <Input
          id="admin_fee"
          inputMode="decimal"
          autoComplete="off"
          className="border-0 p-0 h-auto shadow-none focus-visible:ring-0 px-0"
          value={value}
          onInput={(e) => setValue((e.currentTarget as HTMLInputElement).value)}
          onFocus={() => setValue(format_admin_fee_edit_value(admin_fee))}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              ;(e.currentTarget as HTMLInputElement).blur()
            }
          }}
          onBlur={handleBlur}
          disabled={loading}
        />
      </div>
      {loading && (
        <div class="w-full absolute h-full top-0 left-0 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      )}
    </div>
  )
}

export function should_show_admin_fee_field(
  deduction: Pick<DeductionDetailJson, "kind" | "admin_fee">,
  orgFieldConfigs: Parameters<typeof deduction_detail_org_field_active>[0]
): boolean {
  return deduction.kind !== "repayment" && (deduction_detail_org_field_active(orgFieldConfigs, "admin_fee") || deduction.admin_fee != null)
}

/** Posting date is editable when the org enables it for the detail page, or when a value already exists. */
export function should_show_posting_date_field(
  deduction: Pick<DeductionDetailJson, "posting_date">,
  orgFieldConfigs: Parameters<typeof deduction_detail_org_field_active>[0]
): boolean {
  return deduction_detail_org_field_active(orgFieldConfigs, "posting_date") || deduction.posting_date != null
}

/**
 * The brand's own accounting number for this deduction — Sage's ARCR for Blue Planner brands, a
 * NetSuite internal id or a BC entry number elsewhere. Read-only: integrations write it, and
 * `import_deduction` upserts on `(org_id, remote_accounting_id)`, so an edit could break a feed.
 *
 * Strictly config-gated, unlike the date fields below, which also show whenever a value exists.
 * Most orgs' `remote_accounting_id` holds an opaque sync id nobody wants on the page — Sugo alone
 * has 7,747 of them — so it appears only where a brand asked for it.
 */
export function should_show_accounting_id_field(
  deduction: Pick<DeductionDetailJson, "remote_accounting_id">,
  orgFieldConfigs: Parameters<typeof deduction_detail_org_field_active>[0]
): boolean {
  return (
    deduction_detail_org_field_active(orgFieldConfigs, "remote_accounting_id") &&
    (deduction.remote_accounting_id ?? "").trim() !== ""
  )
}

/** Display name the org gave the field, so "ARCR" shows for brands that call it that. */
export function accounting_id_field_label(
  orgFieldConfigs: Parameters<typeof deduction_detail_org_field_active>[0]
): string {
  const config = orgFieldConfigs.find(
    (c) => c.is_active && c.field_name === "remote_accounting_id" && c.table_contexts.includes("deduction_detail")
  )
  const display = config?.display_name?.trim()
  return display && display !== "" ? display : "Accounting ID"
}

/** Validation date is editable when the org enables it for the detail page, or when a value already exists. */
export function should_show_validated_date_field(
  deduction: Pick<DeductionDetailJson, "validated_date">,
  orgFieldConfigs: Parameters<typeof deduction_detail_org_field_active>[0]
): boolean {
  return deduction_detail_org_field_active(orgFieldConfigs, "validated_date") || deduction.validated_date != null
}

/** Human-readable dispute window state from the server-computed deadline (exported for tests). */
export function format_dispute_window_display(
  dispute_deadline: string | null | undefined,
  now: Date = new Date()
): { text: string; urgent: boolean } | null {
  if (!dispute_deadline) return null
  const deadline = parseISO(dispute_deadline)
  const days = differenceInDays(deadline, now)
  const formatted = format(deadline, "MMM d, yyyy")
  if (days < 0) return { text: `Expired ${formatted}`, urgent: true }
  return { text: `Expires ${formatted} — ${days} days left`, urgent: days <= 30 }
}

/** Read-only aging facts: days old, time in current stage, and the dispute filing window. */
function AgingFields({ deduction }: { deduction: DeductionDetailJson }) {
  const basis = use_days_old_basis()
  const days_old = compute_age_days(deduction, basis)
  const days_in_stage = compute_age_days(deduction, "in_current_stage")
  const dispute_window = format_dispute_window_display(deduction.dispute_deadline)
  return (
    <div class="grid grid-cols-2 gap-4">
      <div>
        <label class="text-gray-500 text-sm block">Days Old</label>
        <div class="text-sm">{days_old != null ? format_days_old_detail(days_old, basis) : "—"}</div>
      </div>
      <div>
        <label class="text-gray-500 text-sm block">Days in Stage</label>
        <div class="text-sm">
          {days_in_stage != null
            ? `${days_in_stage} days in ${format_deduction_status_label(deduction.status_value)}`
            : "—"}
        </div>
      </div>
      {dispute_window && (
        <div class="col-span-2">
          <label class="text-gray-500 text-sm block">Dispute Window</label>
          <div class={classes("text-sm", dispute_window.urgent ? "text-red-600 font-medium" : "")}>
            {dispute_window.text}
          </div>
        </div>
      )}
    </div>
  )
}

export function DeductionDetailsSection({ deduction, onUpdate, className }: DeductionDetailsSectionProps) {
  const { orgFieldConfigs } = OrgConfigState.use()
  const show_admin_fee = should_show_admin_fee_field(deduction, orgFieldConfigs)
  const show_posting_date = should_show_posting_date_field(deduction, orgFieldConfigs)
  const show_validated_date = should_show_validated_date_field(deduction, orgFieldConfigs)
  const show_accounting_id = should_show_accounting_id_field(deduction, orgFieldConfigs)

  return (
    <div class={classes("space-y-4", className)}>
      <h3 class="text-lg font-semibold">{deduction.kind === "repayment" ? "Repayment Details" : "Deduction Details"}</h3>
      <div className="grid grid-cols-2 gap-4">
        <EditField
          id="retailer_name"
          label="Retailer"
          value={deduction.retailer_name ?? ""}
          deductionId={deduction.id}
          onUpdate={onUpdate}
        />
        <DateField
          id="execution_date"
          label="Execution Date"
          value={deduction.execution_date}
          deductionId={deduction.id}
          onUpdate={onUpdate}
        />
        {show_posting_date && (
          <DateField
            id="posting_date"
            label="Posting Date"
            value={deduction.posting_date ?? ""}
            deductionId={deduction.id}
            onUpdate={onUpdate}
          />
        )}
        {show_validated_date && (
          <DateField
            id="validated_date"
            label="Validation Date"
            value={deduction.validated_date ?? ""}
            deductionId={deduction.id}
            onUpdate={onUpdate}
          />
        )}
        {show_admin_fee && (
          <div class="col-span-2">
            <AdminFeeField admin_fee={deduction.admin_fee} deductionId={deduction.id} onUpdate={onUpdate} />
          </div>
        )}
        {deduction.source === "unfi" ? (
          <WarehouseDropdown
            id="dc"
            label="Warehouse"
            value={deduction.dc ?? ""}
            deductionId={deduction.id}
            onUpdate={onUpdate}
          />
        ) : (
          <EditField
            id="dc"
            label="Warehouse"
            value={deduction.dc ?? ""}
            deductionId={deduction.id}
            onUpdate={onUpdate}
          />
        )}
        <EditField
          id="po_number"
          label="PO Number"
          value={deduction.po_number ?? ""}
          deductionId={deduction.id}
          onUpdate={onUpdate}
        />
        {show_accounting_id && (
          <AccountingIdField
            label={accounting_id_field_label(orgFieldConfigs)}
            value={deduction.remote_accounting_id ?? ""}
          />
        )}
        <SelectField
          id="custom_deduction_method"
          label="Method"
          value={deduction.custom_deduction_method ?? ""}
          options={customDeductionMethodOptionsForValue(deduction.custom_deduction_method ?? "")}
          getOptionLabel={format_custom_deduction_method_label}
          deductionId={deduction.id}
          onUpdate={onUpdate}
        />
        {/* UNFI-specific: Show tracking number from WeeklyStatusReportRow when available */}
        {deduction.source === "unfi" && deduction.tracking_number && (
          <TrackingNumberField value={deduction.tracking_number} />
        )}
        {deduction.import_source === "userupload" && (
          <PendingField
            pending={deduction.pending}
            deductionId={deduction.id}
            onUpdate={onUpdate}
          />
        )}
        <div class="col-span-2">
          <div class="text-gray-500 text-sm mb-1">Category</div>
          <CategoryCell deduction={deduction} className="mt-1 w-full" />
          {deduction.category_description && (
            <div class="mt-2 text-sm text-gray-700">{deduction.category_description}</div>
          )}
        </div>
        <DeductionPromotionField
          deduction={deduction}
          onUpdate={onUpdate}
          className="col-span-2"
        />
      </div>
      <Separator />
      <div class="grid grid-cols-2 gap-4">
        <EditField
          id="check_number"
          label="Check Number"
          value={deduction.check_number ?? ""}
          deductionId={deduction.id}
          onUpdate={onUpdate}
        />
        <DateField
          id="check_date"
          label="Check Date"
          value={deduction.check_date ?? ""}
          deductionId={deduction.id}
          onUpdate={onUpdate}
        />
        {deduction.check_amount > 0 && (
          <div>
            <label class="text-gray-500 text-sm block">Check Amount</label>
            <div class="text-sm">{exactCurrencyFormatter(deduction.check_amount, deduction.currency)}</div>
          </div>
        )}
      </div>
      {deduction.kind !== "repayment" && (
        <>
          <Separator />
          <AgingFields deduction={deduction} />
        </>
      )}
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("should_show_admin_fee_field", () => {
    it("shows parsed admin fee even when org field config does not enable it", () => {
      expect(should_show_admin_fee_field({ kind: "deduction", admin_fee: 2.5 }, [])).toBe(true)
    })

    it("hides blank admin fee when org field config does not enable it", () => {
      expect(should_show_admin_fee_field({ kind: "deduction", admin_fee: null }, [])).toBe(false)
    })

    it("does not show admin fee for repayments", () => {
      expect(should_show_admin_fee_field({ kind: "repayment", admin_fee: 2.5 }, [])).toBe(false)
    })
  })

  describe("should_show_accounting_id_field", () => {
    function config(overrides: Record<string, unknown> = {}) {
      return [{
        id: "1",
        org_id: "o",
        field_name: "remote_accounting_id",
        field_type: FieldType.Text,
        display_name: "ARCR",
        display_order: 0,
        is_active: true,
        table_contexts: ["deduction_detail"],
        created_at: "",
        updated_at: "",
        ...overrides,
      }] as any
    }
    const arcr = { remote_accounting_id: "Sales Deductions-ARCR-010493" }

    it("shows when the org enabled it and a value exists", () => {
      expect(should_show_accounting_id_field(arcr as any, config())).toBe(true)
    })

    it("stays hidden without the org field config, unlike the date fields", () => {
      // Most orgs' remote_accounting_id is an opaque sync id, so a value alone is not enough.
      expect(should_show_accounting_id_field(arcr as any, [])).toBe(false)
    })

    it("hides when the config is inactive or scoped to another table", () => {
      expect(should_show_accounting_id_field(arcr as any, config({ is_active: false }))).toBe(false)
      expect(should_show_accounting_id_field(arcr as any, config({ table_contexts: ["deduction_table"] }))).toBe(false)
    })

    it("hides an empty value rather than rendering a blank row", () => {
      expect(should_show_accounting_id_field({ remote_accounting_id: "" } as any, config())).toBe(false)
      expect(should_show_accounting_id_field({ remote_accounting_id: "  " } as any, config())).toBe(false)
      expect(should_show_accounting_id_field({} as any, config())).toBe(false)
    })

    it("labels the field with the org's own name for it", () => {
      expect(accounting_id_field_label(config())).toBe("ARCR")
      expect(accounting_id_field_label(config({ display_name: "  " }))).toBe("Accounting ID")
      expect(accounting_id_field_label([])).toBe("Accounting ID")
    })
  })

  describe("should_show_posting_date_field", () => {
    const posting_date_config = {
      id: "1",
      org_id: "o",
      field_name: "posting_date",
      field_type: FieldType.Date,
      display_name: "Posting Date",
      display_order: 0,
      is_active: true,
      table_contexts: ["deduction_detail"],
      created_at: "",
      updated_at: "",
    }

    it("shows when the org enables posting_date for deduction detail", () => {
      expect(should_show_posting_date_field({ posting_date: null }, [posting_date_config])).toBe(true)
    })

    it("shows an existing posting date even when org field config does not enable it", () => {
      expect(should_show_posting_date_field({ posting_date: "2026-06-24" }, [])).toBe(true)
    })

    it("hides a blank posting date when org field config does not enable it", () => {
      expect(should_show_posting_date_field({ posting_date: null }, [])).toBe(false)
    })
  })

  describe("date_field_feeds_clearing_list", () => {
    it("is true only for the dates the clearing list is computed from", () => {
      expect(date_field_feeds_clearing_list("validated_date")).toBe(true)
      expect(date_field_feeds_clearing_list("posting_date")).toBe(true)
      expect(date_field_feeds_clearing_list("execution_date")).toBe(false)
    })
  })

  describe("should_show_validated_date_field", () => {
    const validated_date_config = {
      id: "1",
      org_id: "o",
      field_name: "validated_date",
      field_type: FieldType.Date,
      display_name: "Validation Date",
      display_order: 0,
      is_active: true,
      table_contexts: ["deduction_detail"],
      created_at: "",
      updated_at: "",
    }

    it("shows when the org enables validated_date for deduction detail", () => {
      expect(should_show_validated_date_field({ validated_date: null }, [validated_date_config])).toBe(true)
    })

    it("shows an existing validation date even when org field config does not enable it", () => {
      expect(should_show_validated_date_field({ validated_date: "2026-06-19" }, [])).toBe(true)
    })

    it("hides a blank validation date when org field config does not enable it", () => {
      expect(should_show_validated_date_field({ validated_date: null }, [])).toBe(false)
    })
  })

  describe("format_dispute_window_display", () => {
    const now = new Date("2026-07-15T12:00:00Z")

    it("returns null without a deadline", () => {
      expect(format_dispute_window_display(null, now)).toBeNull()
      expect(format_dispute_window_display(undefined, now)).toBeNull()
    })

    it("marks expired windows urgent", () => {
      const display = format_dispute_window_display("2026-07-01", now)!
      expect(display.text).toContain("Expired")
      expect(display.urgent).toBe(true)
    })

    it("marks windows within 30 days urgent", () => {
      const display = format_dispute_window_display("2026-08-01", now)!
      expect(display.text).toContain("days left")
      expect(display.urgent).toBe(true)
    })

    it("is not urgent beyond 30 days", () => {
      const display = format_dispute_window_display("2026-12-01", now)!
      expect(display.urgent).toBe(false)
    })
  })

  describe("admin fee formatting", () => {
    it("formats admin fee as currency", () => {
      expect(format_admin_fee_display_value(2.5)).toBe("$2.50")
    })

    it("parses plain and formatted dollar inputs", () => {
      expect(parse_admin_fee_input_value("2.50")).toBe(2.5)
      expect(parse_admin_fee_input_value("$1,234.56")).toBe(1234.56)
      expect(parse_admin_fee_input_value("")).toBeNull()
      expect(parse_admin_fee_input_value("two dollars")).toBeUndefined()
    })
  })
}
